// sim/colonyExample.ts — headless autonomous colony demo + the world builder.
//
// `buildColonyWorld(config)` wires the SoA store, grid, RNG, colony layer, and
// the per-caste behaviour loop, seeds colonies + food deterministically, and
// returns a ready world. `runHeadless` advances it. The determinism test and
// the CLI both import this, so the demo and the test exercise ONE code path.
//
// Run it directly:  node src/sim/colonyExample.ts --colonies 2 --ticks 200 --map

import {
  KIND, CASTE, CASTE_COUNT, CHAMBER,
  GENE_TYPE, GENE_TYPE_COUNT, GENE_TRAIT, GENE_TRAIT_COUNT,
  RARITY, RARITY_COUNT, NIL,
  NEED, NEED_COUNT,
} from '../types.ts';
import type {
  ColonyWorldConfig,
  CasteConfig,
  ColonyConfig,
  GeneConfig,
  ResourceConfig,
  ColonyAIConfig,
  PersonalityConfig,
  HiveConfig,
  EcosystemEventConfig,
  EntityId,
  Species,
  Caste,
  GeneType,
  Need,
  Trait,
  System,
  Tick,
} from '../types.ts';
import { TRAIT_COUNT } from '../types.ts';
import { Rng } from '../rng.ts';
import { fnvInit, fnvU32, hashHex } from '../hash.ts';
import { EntitySystem } from '../engine/EntitySystem.ts';
import { WorldGrid } from '../engine/WorldGrid.ts';
import { GameLoop } from '../engine/GameLoop.ts';
import { GreedyPathing } from '../engine/Pathing.ts';
import { ResourceSystem } from './resources.ts';
import { ColonySystem, DEFAULT_QUEEN_CONFIG } from './colony.ts';
import { OrganismSystem } from './organisms.ts';
import { TaskQueue } from './taskQueue.ts';
import type { AutonomyCtx } from './taskQueue.ts';
import { ColonyAISystem } from './colonyAI.ts';
import { EcosystemEventSystem, neutralModifiers } from './ecosystemEvents.ts';
import type { EcosystemModifiers } from './ecosystemEvents.ts';
import type { WildlifeSystem } from './wildlife.ts';
import { ColonyPersonality, TRAIT_NAME } from './personality.ts';
import { GenomeSystem, baseProfile, geneFromTraits } from './genome.ts';
import type { CasteGene } from './genome.ts';
import { CASTE_NAME } from './casteNames.ts';
import { GENE_TYPE_NAME } from './geneData.ts';
import { SpriteCache } from './creatureSprite.ts';
import { HiveSystem } from './hive.ts';
import { CarryingCapacitySystem } from './capacity.ts';
import type { CapacityConfig } from './capacity.ts';
import { GeneInventory } from './genes.ts';
import type { GeneId } from './genes.ts';
import { RARITY_NAME } from './geneData.ts';
import { LeaderboardSystem } from './leaderboard.ts';
import { SCORE_DIM, SCORE_DIM_COUNT } from '../types.ts';
import type { LeaderboardConfig, ScoreDim } from '../types.ts';

// ---------------------------------------------------------------------------
// World container.
// ---------------------------------------------------------------------------

export interface ColonyWorld {
  readonly config: ColonyWorldConfig;
  readonly rng: Rng;
  readonly entities: EntitySystem;
  readonly grid: WorldGrid;
  readonly loop: GameLoop;
  readonly resources: ResourceSystem;
  readonly colonies: ColonySystem;
  readonly organisms: OrganismSystem;
  readonly genes: GeneInventory;
  readonly genomes: GenomeSystem;
  /** Autonomy: the pooled task scheduler. */
  readonly tasks: TaskQueue;
  /** Autonomy: the high-level decision engine. */
  readonly ai: ColonyAISystem;
  /**
   * Emergent per-colony personality (accumulated from the AI's decisions, then
   * fed back as a small need bias). OPTIONAL so sibling builders that extend
   * ColonyWorld (ecosystem / evolution) need no edits; `buildColonyWorld` always
   * provides it, and every read site (hash, CLI) guards on it.
   */
  readonly personality?: ColonyPersonality;
  /** Read-only colony-evolution leaderboard (runs last; observes only). */
  readonly leaderboard: LeaderboardSystem;
  /**
   * Physical hive: chambers + tunnels + the territory tile map. Optional —
   * present in the full colony world; sibling worlds (ecosystem / evolution that
   * extend ColonyWorld) omit it, so every read site guards on it.
   */
  readonly hive?: HiveSystem;
  /**
   * Render-only creature-sprite cache, keyed by genome snapshot (`profileRef`).
   * OPTIONAL so sibling builders that extend/call buildColonyWorld are unaffected
   * and need no edits. Pure function of the genome (no RNG) and deliberately NOT
   * folded into computeColonyStateHash — it is a UI/debug concern, so it can never
   * perturb sim determinism. The canvas (web/render.ts) consumes it; assembly is
   * once per distinct profile, shared by every organism carrying it.
   */
  readonly sprites?: SpriteCache;
  /**
   * The shared autonomy/organism context. Exposed so an outer layer can attach
   * ADDITIVE seams without forking the builder — e.g. set `ctx.mutation` to a
   * MutationEnergySink to count per-colony kills. Engine behaviour is byte-identical
   * unless such a seam is set (the default world never sets one). Optional so sibling
   * worlds that extend ColonyWorld without re-exporting ctx still satisfy the type;
   * `buildColonyWorld` always provides it.
   */
  readonly ctx?: AutonomyCtx;
  /**
   * Dynamic-ecosystem-event scheduler — present ONLY when config.events is set.
   * Absent ⇒ the world is byte-identical to a pre-events colony world (every read
   * site, incl. the hash, guards on it). OPT-IN seasonal pressure: the colony world
   * has no wildlife, so the wildlife-only onset effects (cull / predator burst) are
   * inert; only the RESOURCE-REGEN, GENE-DROP and organism-drain modifier seams act.
   * Same field/type as EcosystemWorld.events so the ecosystem builder stays compatible.
   */
  readonly events?: EcosystemEventSystem | undefined;
  /** The live event-modifier struct (present iff `events` is). */
  readonly eventMods?: EcosystemModifiers | undefined;
  /**
   * Carrying-capacity governor (OPTIONAL — present iff `config.capacity` is set).
   * Absent ⇒ the colony world keeps its fixed `maxPopulation` cap, byte-identical
   * to before. Same field/type as EcosystemWorld.capacity.
   */
  readonly capacity?: CarryingCapacitySystem | undefined;
  /**
   * Whether the OPT-IN per-creature movement-speed feature is on for this world
   * (mirrors `config.colony.creatureSpeed === true`). Drives the GUARDED `moveAccum`
   * fold in `computeColonyStateHash`: false ⇒ the fold is skipped ⇒ the hash is
   * byte-identical to a pre-feature world. Always present (default false) so the hash
   * guard reads a definite boolean.
   */
  readonly creatureSpeed: boolean;
}

// ---------------------------------------------------------------------------
// Optional events config (the modifier seam, opt-in). `ColonyWorldConfig` lives
// in types.ts and carries no `events` field; this widened view adds it WITHOUT
// touching the shared type, so a config that omits `events` is exactly today's
// config and the world stays byte-identical. Mirrors EcosystemWorldConfig.events.
// ---------------------------------------------------------------------------

/** `ColonyWorldConfig` plus the opt-in dynamic-ecosystem-event seam. */
export interface ColonyWorldConfigWithEvents extends ColonyWorldConfig {
  /** Dynamic ecosystem events (optional — ABSENT ⇒ the world is byte-identical). */
  readonly events?: EcosystemEventConfig | undefined;
  /**
   * Carrying-capacity governor (optional — ABSENT ⇒ the fixed `maxPopulation` cap,
   * byte-identical). When set (e.g. `defaultCapacityConfig(colony.maxPopulation)`)
   * the colony world's population becomes income/storage/hive-driven, matching the
   * ecosystem world. The colony world has no region-projects layer, so the project
   * input is 0. Pass a CapacityConfig to enable.
   */
  readonly capacity?: CapacityConfig | undefined;
}

// ---------------------------------------------------------------------------
// Default tuning (shared by the CLI and tests).
// ---------------------------------------------------------------------------

/** Per-caste defaults, indexed by CASTE.* (Worker, Soldier, Flyer, Specialist). */
export function defaultCasteConfigs(): CasteConfig[] {
  const cfgs: CasteConfig[] = [];
  // PACING + WORLD-SCALE: startEnergy is endurance away from the nest (a member
  // adjacent to its nest refuels to full each tick — organisms.ts; energyDecay is
  // 1/tick, so startEnergy is the round-trip range budget). Two compounding
  // pressures push it up: the slowed spawn cadence (each body must last ~5x longer
  // between replacements) AND the ~7.4x-larger default world (food nodes scatter
  // farther from the nest, so foraging round-trips are physically longer). The
  // WORLD-SCALE fix is endurance, NOT perception: senseRadius is held EXACTLY where
  // it was (exploration must stay costly — a forager still has to wander to find a
  // node), and energyDecay stays at its 1/tick floor. startEnergy rises ~55-60% so
  // a forager survives the longer haul to a sparse node and back. HP/attack are
  // unchanged — combat lethality is a separate axis, deliberately left alone.
  cfgs[CASTE.Worker] = {
    startEnergy: 165, energyDecay: 1, startHp: 8,
    senseRadius: 6, carryCapacity: 5, attackPower: 0, roamRadius: 0,
  };
  cfgs[CASTE.Soldier] = {
    // roamRadius 4 -> 6: patrols cover more ground on the big map so a Soldier
    // leash actually screens the nest's wider neighbourhood (still LOCAL, not
    // map-spanning). Endurance up so patrols/sallies don't starve mid-circuit.
    startEnergy: 230, energyDecay: 1, startHp: 20,
    senseRadius: 5, carryCapacity: 0, attackPower: 4, roamRadius: 6,
  };
  cfgs[CASTE.Flyer] = {
    // Scouts now relay Expand sites EXPAND_REACH (16) tiles out — they must reach
    // the candidate and return, so endurance climbs with the bigger world.
    startEnergy: 210, energyDecay: 1, startHp: 6,
    senseRadius: 8, carryCapacity: 0, attackPower: 0, roamRadius: 0,
  };
  cfgs[CASTE.Specialist] = {
    // Crisis foragers haul the longest carries (carryCapacity 6) — biggest absolute
    // endurance bump so an emergency supply run completes across the larger map.
    startEnergy: 200, energyDecay: 1, startHp: 12,
    senseRadius: 6, carryCapacity: 6, attackPower: 0, roamRadius: 0,
  };
  return cfgs;
}

export function defaultColonyConfig(): ColonyConfig {
  // PACING — population growth + larvae maturation. spawnCooldown is the ticks
  // between a colony raising successive brood; it doubles as the "maturation time"
  // lever (broodReadyAt is a derived count, not a timed entity). 6 → 30 (5x) turns
  // an RTS-fast hatchery into a gradual nursery: a colony climbs to its cap over
  // ~hundreds of ticks, not dozens. spawnCost/maxPopulation are untouched — the cap
  // is a ceiling, not a rate, so the END state can still be a big colony; only the
  // CLIMB is slowed. initialFood keeps a small founding buffer (~3 spawns).
  // NOTE: reproduction RATE + survival-under-predation on the bigger world is the
  // carrying-capacity layer's job (src/sim/capacity.ts, claim CZAB7Z), NOT retuned
  // here — keeping these defaults stable keeps the colony/population/generation tests
  // (and every world hash) unchanged. The world-scale refactor only enlarges the map
  // + food supply + endurance + expansion reach + wildlife territory.
  return { spawnCost: 8, spawnCooldown: 30, maxPopulation: 60, initialFood: 30, lowFoodThreshold: 6 };
}

/**
 * Resource defaults (shared by the CLI and tests). PACING — resource-gathering
 * speed. Food nodes regenerate on a `regenDelay` clock back up to `regenAmount`.
 * regenDelay 25 → 70 (2.8x) and regenAmount 12 → 9 (0.75x) together cut the food
 * supply rate ~3.7x: forage routes become scarce, deliberate, and worth defending,
 * so supply chains emerge over time instead of every tile refilling instantly.
 */
export function defaultResourceConfig(): ResourceConfig {
  return { regenDelay: 70, regenAmount: 9 };
}

/**
 * PACING — real-time playback tiers (the orthogonal axis to the in-sim slowdown
 * above). The in-sim constants make WORLD EVENTS ~5x rarer per tick; these tiers
 * scale how many ticks elapse per second, which is the only lever for motion
 * itself (an organism steps 1 tile/tick — the determinism quantum — so movement
 * cannot be slowed in-sim without a sub-tick gate that would desync pathing).
 *   • observation 0.5x — study a single nest / watch one hunt up close.
 *   • intended    1.0x — the calm, gradual default the rebalance is tuned for.
 *   • accelerated 2.0x — normal play once you know the colony.
 *   • management  5.0x — late-game catch-up across a mature, multi-colony map.
 * The web loop multiplies its base ticks/sec (see web/save.ts) by the active tier.
 */
export const SPEED_TIERS = {
  observation: 0.5,
  intended: 1,
  accelerated: 2,
  management: 5,
} as const;
export type SpeedTier = keyof typeof SPEED_TIERS;

/** Gene-loot defaults (shared by the CLI and tests). */
export function defaultGeneConfig(): GeneConfig {
  // PACING — gene-acquisition speed. Genes drop on kills/deaths; at the old rates a
  // colony amassed a chest of genes in a few hundred ticks, so "evolution" was
  // routine rather than a milestone. combat 35 → 18 halves war-loot, while death
  // 6 → 4 keeps a steady trickle from natural attrition (so evolution stays present
  // even in the calmer, less-warlike pace where combat kills are rarer). Net ~5x
  // slower accrual, each drop earned. Capacity is unchanged.
  return { perColonyCapacity: 128, combatDropChancePct: 18, deathDropChancePct: 4 };
}

/** Colony-AI defaults (shared by the CLI and tests). */
export function defaultColonyAIConfig(): ColonyAIConfig {
  // PACING — decision cadence, task completion, territory expansion, auto-evolution.
  //  • decisionInterval 5 → 24 (~5x): the colony re-reads its needs and re-posts
  //    tasks far less often, so posture shifts (forage↔defend↔expand) and the whole
  //    task economy unfold gradually instead of snapping every few ticks. This is
  //    the main brake on the "all-soldier by tick 200" militarisation rush.
  //  • expandPopThreshold 18 → 26 (~1.4x): a colony must be well-established before
  //    it throws out a hive node. Only a MODEST bump — population already grows ~5x
  //    slower, so reaching the threshold is itself the pacing; over-raising it (to
  //    e.g. 34) pushed expansion past what colonies reach in a session → it never
  //    fired. 26 sits just under a thriving colony's long-run size, so expansion
  //    arrives late and earned, not instantly and not never.
  //  • evolutionMinGenes 4 → 8 (2x): auto-splice/evolve waits for a real gene bank.
  //    Same logic — genes already accrue ~5x slower, so a big additional multiplier
  //    would mean evolution never triggers; 8 lands it as a mid/late-run milestone.
  // hiveDividend / maxHiveNodes / tasksPerColony are structural, left as-is.
  // scoutTimeout 4 (decision cadences): with decisionInterval 24 that's ~96 ticks of
  // a colony WANTING to expand but unable to spare a Flyer before it founds directly.
  // Modest — long enough that a Flyer-healthy colony always relays a Scout first (so
  // the common path is unchanged), short enough that a crowded-but-poor colony is not
  // stuck for the whole run. haulNestThreshold 6: a Storage-holding colony hauls when
  // its nest food dips within 6 of its population (a real shortfall, not a full larder).
  return {
    decisionInterval: 24,
    lowFood: 10,
    threatRadius: 6,
    expandPopThreshold: 26,
    maxHiveNodes: 4,
    hiveDividend: 12,
    evolutionMinGenes: 8,
    tasksPerColony: 16,
    scoutTimeout: 4,
    haulNestThreshold: 6,
  };
}

/**
 * Colony-personality defaults (shared by the CLI and tests). emergeThreshold is
 * a handful of consistent decisions (≈6 matching priorityWeight-2 choices) so a
 * temperament forms within a run but not on the first decision; biasStrength is
 * deliberately small so the feedback only tips ambiguous calls, never survival.
 */
export function defaultPersonalityConfig(): PersonalityConfig {
  return {
    priorityWeight: 2,
    switchWeight: 2,
    evolveWeight: 1,
    killWeight: 2,
    soldierRatioPct: 40,
    emergeThreshold: 12,
    biasStrength: 3,
    // Structural signals: a colony's genes + claimed territory shape its identity
    // alongside its decisions. Sized near priorityWeight so they meaningfully tilt
    // the dominant trait without instantly overpowering the decision stream.
    geneWeight: 2,
    territoryWeight: 1,
  };
}

/**
 * Leaderboard defaults (shared by the CLI and tests). Weights are ordered by
 * SCORE_DIM.*: GenomeComplexity, GeneRarity, TerritoryControl, ColonySize,
 * EcosystemInfluence, RegionDominance, Discovery. MUST be length SCORE_DIM_COUNT
 * (7) — a shorter vector makes the instant-score fold read undefined → NaN.
 */
export function defaultLeaderboardConfig(): LeaderboardConfig {
  return { cadence: 5, lifetimeDivisor: 16, weights: [3, 4, 3, 2, 1, 2, 5] };
}

/** Physical-hive defaults (shared by the CLI and tests). */
export function defaultHiveConfig(): HiveConfig {
  // PACING — physical-hive growth (a facet of territory expansion).
  //  • effectInterval 10 → 28 (~2.8x): chamber effects (storage yield, mutation,
  //    military strikes) pulse on a slower clock so the hive feels like architecture,
  //    not a turret cycling every few ticks.
  //  • mutationInterval 20 → 80 (4x): the Mutation chamber forges genes far less
  //    often — structural evolution becomes a long-horizon investment.
  //  • upgradeGeneThreshold 6 → 8 (~1.3x): chamber upgrades demand a deeper gene
  //    bank — only a modest bump, since genes already accrue ~5x slower (the same
  //    don't-double-count rule as evolutionMinGenes).
  // Yields/urgencies/damage are magnitudes, not rates — kept so chambers still
  // matter when they do fire.
  // Chamber maintenance (the minimal honest damage model): non-core chambers carry a
  // small integrity meter (repairMax 100) that wears by chamberDecay each effect
  // cadence (~28 ticks). At/below repairUrgency 40 a chamber is DEGRADED and the AI
  // posts a Repair, which restores repairAmount 50. With decay 6 a fresh L1 outlying
  // room takes ~10 cadences to degrade, so Repair is occasional upkeep, not a churn.
  // nurseSpawnBoost 4: a Nurse shaves 4 ticks off the next brood. haulAmount 6: a
  // Haul moves 6 food from Storage into the store (paired with haulNestThreshold).
  return {
    roomRadius: 1,
    maxChambersPerColony: 5,
    tunnelCarves: true,
    upgradeGeneThreshold: 8,
    maxChamberLevel: 3,
    nurseryDividend: 3,
    mutationInterval: 80,
    effectInterval: 28,
    storageYield: 3,
    storageUrgency: 8,
    militaryUrgency: 8,
    militaryDamage: 2,
    militaryRadius: 2,
    liveThreatRadius: 3,
    repairMax: 100,
    chamberDecay: 6,
    repairAmount: 50,
    repairUrgency: 40,
    nurseSpawnBoost: 4,
    haulAmount: 6,
  };
}

// ---------------------------------------------------------------------------
// World builder.
// ---------------------------------------------------------------------------

/**
 * Salt for the PER-TICK colony-AI decision stream (DESIGN.md §6: per-system PRNG
 * stream isolation). The worldgen `rng` (walls/food/colony placement) and the AI's
 * per-tick decision randomness MUST be independent streams, so adding/removing a
 * worldgen entity (e.g. a different food count) cannot shift the AI's sequence.
 * This salt is DISTINCT from every other stream's (wildlife 0x9e3779b9 / events
 * 0x5af3107a / apex 0x7a5c0fe1 / box 0x85ebca6b / rival 0x5bd1e995), so the colony
 * AI stream stays disjoint from all of them across both world builders.
 */
export const AI_STREAM_SALT = 0xc2b2ae35;

export function buildColonyWorld(config: ColonyWorldConfigWithEvents): ColonyWorld {
  // The WORLDGEN stream: drawn ONLY during deterministic placement below (walls,
  // food, colony nests). Isolated from the per-tick AI stream so worldgen
  // entity-count changes never perturb the colony's decision randomness.
  const rng = new Rng(config.seed);
  // The PER-TICK decision stream for ColonyAISystem (expansion-direction rolls),
  // derived from the seed via AI_STREAM_SALT so it is independent of worldgen.
  const aiRng = new Rng((config.seed ^ AI_STREAM_SALT) >>> 0);

  // Dynamic ecosystem events are OPT-IN here (the BACKLOG "seasonal pressure"
  // seam). When config.events is set we create ONE shared modifier struct (the
  // event system WRITES it; resources/organisms/castes READ it). When unset,
  // `mods` stays undefined and every seam takes its neutral path, so the world is
  // BYTE-IDENTICAL to a pre-events colony build — this is the #1 requirement.
  const mods: EcosystemModifiers | undefined =
    config.events !== undefined ? neutralModifiers() : undefined;

  // Peak live organisms is bounded by colonies * peak-per-colony; despawn recycles
  // ids, so capacity need only cover the peak plus food plus headroom. Founders may
  // exceed the steady-state cap, so size against whichever is larger. Events add NO
  // entities here (the colony world has no wildlife; the cull/burst onset effects are
  // inert — see buildColonyWorld's event wiring), so the bound is unchanged whether
  // events are on or off — keeping the entity layout byte-identical.
  const peakPerColony = Math.max(config.colony.maxPopulation, config.startMembers);
  const capacity = config.colonies * peakPerColony + config.food + 16;

  const entities = new EntitySystem(capacity);
  const grid = new WorldGrid(entities, config.width, config.height, config.maxPerTile);
  // The resource regen seam reads `mods.foodRegenPct` (neutral 100 ⇒ unchanged). When
  // events are OFF, `mods` is undefined and this call is byte-identical to today
  // (ResourceSystem's 5th arg defaults to the neutral path).
  const resources = new ResourceSystem(entities, grid, config.resource, config.food, mods);

  // Per-caste base profiles (Flyer flies; the rest walk) → the genome system,
  // which resolves gene loadouts into the profiles that future spawns inherit.
  const baseProfiles = config.castes.map((cfg, caste) => baseProfile(cfg, caste === CASTE.Flyer));
  const genomes = new GenomeSystem(baseProfiles);

  // Render-only sprite cache. Sized with headroom over the current snapshot count;
  // gene application publishes further snapshots as the run evolves and the cache's
  // slots array grows dynamically (getOrAssemble tolerates a profileRef past the
  // initial size), so this is only a starting capacity, never a hard cap.
  const sprites = new SpriteCache(Math.max(genomes.snapshotCount, 64));

  // Queen-persistence tuning: caller-supplied (e.g. the demo's --queen-* flags) or the
  // default. Omitted ⇒ DEFAULT_QUEEN_CONFIG, exactly the ctor's own default ⇒ byte-identical.
  const colonies = new ColonySystem(
    entities, grid, genomes, config.colony, config.colonies, config.queen ?? DEFAULT_QUEEN_CONFIG,
  );
  const pathing = new GreedyPathing(grid);
  const geneConfig = config.gene ?? defaultGeneConfig();
  const genes = new GeneInventory(config.colonies, geneConfig);

  // --- Autonomy: the task scheduler + the decision engine. ---
  const aiConfig = config.ai ?? defaultColonyAIConfig();
  const tasks = new TaskQueue(config.colonies, capacity, aiConfig.tasksPerColony);
  // Emergent personality: accumulates the AI's repeated decisions into a
  // temperament that then biases future decisions (a self-reinforcing loop).
  const personality = new ColonyPersonality(
    config.colonies, config.personality ?? defaultPersonalityConfig(),
  );
  // ColonyAISystem draws per-tick decision randomness (expansion direction), so it
  // takes the ISOLATED aiRng — NOT the worldgen `rng` — keeping worldgen placement
  // and per-tick decisions on disjoint streams (DESIGN.md §6).
  const ai = new ColonyAISystem(
    colonies, entities, grid, aiRng, tasks, genes, aiConfig, config.colony.maxPopulation, personality,
  );

  // --- Physical hive: realises the AI's founded nodes into on-map chambers. ---
  const hive = new HiveSystem(colonies, ai, entities, grid, rng, genes, config.hive ?? defaultHiveConfig());
  // Late-bind the hive bridge so the AI can post hive-tending tasks (Nurse / Haul /
  // Repair) against the colony's real chambers and resolve them on arrival.
  ai.attachHive(hive);

  // The organism loop reads the autonomy-widened ctx (castes.ts stays untouched);
  // the Scout/Expand pursuit callbacks route arrivals back into the AI/hive store,
  // and the Nurse/Haul/Repair callbacks route a tending Worker's arrival into the hive.
  const ctx: AutonomyCtx = {
    entities, grid, rng, colonies, resources, genomes, pathing,
    clock: { tick: 0 }, genes, geneConfig,
    // Gene-drop seam: castes.ts reads `eventMods.geneDropPct` (neutral 100) and
    // organisms.ts reads `eventMods.organismDrain` (neutral 0). When events are OFF
    // `mods` is undefined, so `ctx.eventMods` is undefined — exactly today's ctx
    // (both readers guard on `!== undefined`), keeping the world byte-identical.
    eventMods: mods,
    // Per-creature movement speed (opt-in). When config.colony.creatureSpeed is set,
    // `advance` (castes.ts) spends each genome's fixed-point `speed` through the
    // sub-tick accumulator. Unset ⇒ false/undefined ⇒ the original integer stepsPerTick
    // path runs and the world stays byte-identical (the determinism gate).
    creatureSpeed: config.colony.creatureSpeed === true,
    tasks, onExpand: ai.onExpand, onScout: ai.onScout,
    onNurse: ai.onNurse, onHaul: ai.onHaul, onRepair: ai.onRepair,
  };
  const organisms = new OrganismSystem(ctx);

  // --- Read-only standings: scores + ranks colonies. Runs LAST and writes only
  //     its own store, so it can't perturb any other system or the RNG.
  const leaderboard = new LeaderboardSystem(
    colonies, ai, genes, config.leaderboard ?? defaultLeaderboardConfig(),
  );

  // --- Sparse walls (~6%), deterministic; drawn first so placement avoids them.
  const wallTarget = Math.floor(grid.size * 0.06);
  let wallsPlaced = 0;
  let guard = 0;
  const guardMax = grid.size * 4;
  while (wallsPlaced < wallTarget && guard++ < guardMax) {
    const x = rng.int(config.width);
    const y = rng.int(config.height);
    if (grid.isPassable(x, y)) {
      grid.setPassable(x, y, false);
      wallsPlaced++;
    }
  }

  // --- Food nodes on passable tiles. ---
  placeOnPassable(rng, grid, config.food, (x, y) => {
    resources.addFood(x, y, config.foodAmount);
  });

  // --- Colony nests + founding members on passable tiles. ---
  placeOnPassable(rng, grid, config.colonies, (x, y) => {
    const cid = colonies.createColony(x, y);
    colonies.seedInitialMembers(cid, config.startMembers);
  });

  // Queen preservation: colony 0 is the PLAYER — it can be ground down to its queen
  // but never goes extinct (emergency recovery; see colony.ts). Rivals are unprotected.
  if (config.colonies > 0) colonies.protectColony(0);

  // --- Dynamic events (opt-in): the scheduler that mutates `mods`. Built on its
  //     OWN isolated RNG (the SAME seed-derivation the ecosystem world uses for
  //     events: seed ^ 0x5af3107a) so it is independent of the colony worldgen +
  //     AI streams — enabling events never perturbs either beyond the intended
  //     modifier effects. The colony world has NO wildlife, so the wildlife-only
  //     onset effects (cull / predator burst) are routed to a no-op adapter and are
  //     inert; only the RESOURCE-REGEN (foodRegenPct, bloom), GENE-DROP (geneDropPct)
  //     and organism-drain seams act. Absent ⇒ nothing is constructed and the world
  //     is byte-identical to a pre-events build. ---
  let events: EcosystemEventSystem | undefined;
  if (config.events !== undefined && mods !== undefined) {
    const eventRng = new Rng((config.seed ^ 0x5af3107a) >>> 0);
    // No wildlife in the colony world: a zero-population, inert WildlifeSystem stand-in
    // so the event system's wildlife-only effects are no-ops. population*()==0 makes the
    // onset cull early-return (zero rng draws); spawnWildlife/killWildlife do nothing, so
    // a PredatorMigration burst adds NO entities (it only draws from the isolated event
    // rng while probing tiles). Closures are built ONCE here, never per-tick.
    events = new EcosystemEventSystem(
      entities, grid, noWildlife(), resources, mods, eventRng, config.events,
    );
  }

  // --- Fixed system order:
  //   [events] → colony-ai (decide + enqueue) → scheduler (assign open tasks) →
  //   organisms (act, running assigned tasks first) → colonies (spawn) →
  //   resources (regen) → hive (realise founded nodes into chambers).
  // Events run FIRST (mirroring the ecosystem world) so the modifiers it sets apply
  // to every system this tick. The scheduler runs AFTER the AI enqueues and BEFORE
  // organisms act, so a task posted this tick can be picked up the same tick —
  // deterministically. The hive runs LAST so any node the AI founds THIS tick is
  // realised into a physical chamber within the same tick.
  const scheduler: System = {
    name: 'task-scheduler',
    update(_tick: Tick): void {
      tasks.assignOpenTasks(ctx);
    },
  };

  // CARRYING CAPACITY (opt-in): when `config.capacity` is set, an income/storage/hive-
  // driven governor replaces the fixed `maxPopulation` cap (matching the ecosystem
  // world). Both hive signals feed it: founded AI nodes (`hiveCountAt`) AND the physical
  // chambers the `HiveSystem` actually realises (`chamberCountOf`) — a colony that has
  // BUILT galleries earns ceiling for that infrastructure, not just for claiming the
  // node. No region-projects/territory layers here, so those inputs stay 0. Late-bound
  // into the colony spawn loop + AI; recomputes FIRST each tick so the spawn loop sees a
  // fresh ceiling. ABSENT ⇒ no system, fixed cap ⇒ byte-identical (the determinism tests
  // don't set it; when set, both replicas wire chambers identically ⇒ still deterministic).
  let capacityGov: CarryingCapacitySystem | undefined;
  if (config.capacity !== undefined) {
    capacityGov = new CarryingCapacitySystem(colonies, entities, grid, config.capacity, {
      hiveNodesOf: (cid) => ai.hiveCountAt(cid),
      hiveChambersOf: (cid) => hive.chamberCountOf(cid),
      projectsOf: () => 0,
    });
    colonies.attachReproduction(capacityGov);
    ai.attachCapacity(capacityGov);
  }

  const loop = new GameLoop();
  if (events !== undefined) loop.add(events);
  if (capacityGov !== undefined) loop.add(capacityGov); // recompute the cap before the spawn loop
  loop.add(ai).add(scheduler).add(organisms).add(colonies).add(resources).add(hive).add(leaderboard);

  return {
    config, rng, entities, grid, loop, resources, colonies, organisms,
    genes, genomes, tasks, ai, personality, leaderboard, hive, sprites, ctx,
    events, eventMods: mods, capacity: capacityGov,
    creatureSpeed: config.colony.creatureSpeed === true,
  };
}

/**
 * A zero-population, inert WildlifeSystem stand-in for the wildlife-less colony
 * world. The EcosystemEventSystem touches a WildlifeSystem only through five seams
 * (population / populationOf / killWildlife / spawnWildlife / species[]); here they
 * are all no-ops on an empty census, so the wildlife-only onset effects (cull +
 * predator burst) do nothing — keeping events in the colony world to the resource-
 * regen + gene-drop modifiers only. The full WildlifeSystem class carries private
 * fields, so we satisfy the parameter type via a structural cast; nothing in the
 * colony world ever calls any other member. Built once (not per-tick).
 */
function noWildlife(): WildlifeSystem {
  const stub = {
    species: new Uint8Array(0),
    population(): number {
      return 0;
    },
    populationOf(_sp: Species): number {
      return 0;
    },
    killWildlife(_id: EntityId): void {
      /* no wildlife to kill */
    },
    spawnWildlife(_sp: Species, _x: number, _y: number): EntityId {
      // No wildlife layer: spawn nothing (adds no entities to the colony world).
      return NIL;
    },
  };
  return stub as unknown as WildlifeSystem;
}

/**
 * The headline example gene: a Limb-type gene with a Lightweight trait. Slotted
 * into a caste's genome it routes onto the Movement channel and makes future
 * members faster (more steps/tick). "Worker becomes faster due to leg gene."
 */
export function legGene(): CasteGene {
  return geneFromTraits('Sprinter Legs', GENE_TYPE.Limb, [GENE_TRAIT.Lightweight], RARITY.Common);
}

/** Find a passable tile (deterministic, RNG-driven) and invoke `place(x,y)`. */
function placeOnPassable(
  rng: Rng,
  grid: WorldGrid,
  count: number,
  place: (x: number, y: number) => void,
): void {
  for (let i = 0; i < count; i++) {
    let x = 0;
    let y = 0;
    let tries = 0;
    do {
      x = rng.int(grid.width);
      y = rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (grid.isPassable(x, y)) place(x, y);
  }
}

/** Advance the world by `ticks` ticks. */
export function runHeadless(world: ColonyWorld, ticks: number): void {
  world.loop.step(ticks);
}

// ---------------------------------------------------------------------------
// Deterministic state fingerprint (colony + organism state).
// ---------------------------------------------------------------------------

export function computeColonyStateHash(world: ColonyWorld): number {
  const e = world.entities;
  const colonies = world.colonies;
  let h = fnvInit();
  h = fnvU32(h, world.loop.tick);

  // Colony roots.
  h = fnvU32(h, colonies.count);
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, colonies.foodAt(cid));
    h = fnvU32(h, colonies.populationAt(cid));
    h = fnvU32(h, colonies.spawnCursorAt(cid));
    h = fnvU32(h, colonies.spawnCooldownAt(cid));
    h = fnvU32(h, colonies.homeXAt(cid));
    h = fnvU32(h, colonies.homeYAt(cid));
    h = fnvU32(h, colonies.spawnBiasAt(cid));
    h = fnvU32(h, colonies.isAlive(cid) ? 1 : 0);
    h = fnvU32(h, colonies.generationAt(cid));
    for (let k = 0; k < CASTE_COUNT; k++) h = fnvU32(h, colonies.casteCountAt(cid, k as Caste));
  }
  h = fnvU32(h, colonies.generationsAdvancedTotal);

  // Larva life-stage (only when the egg→larva→adult pipeline is active): fold each
  // colony's developing brood — the pending count plus every larva's (hatchTick,
  // caste) in FIFO order — so an incubating colony is load-bearing for the hash. The
  // adults that hatch are already covered via population/casteCount above; this pins
  // the IN-FLIGHT brood. Guarded so an instant-spawn world stays byte-identical.
  if (colonies.larvaMaturationTicks > 0) {
    for (let cid = 0; cid < colonies.count; cid++) {
      const n = colonies.larvaPendingAt(cid);
      h = fnvU32(h, n);
      for (let i = 0; i < n; i++) {
        h = fnvU32(h, colonies.larvaHatchTickAt(cid, i));
        h = fnvU32(h, colonies.larvaCasteAt(cid, i));
      }
    }
  }

  // Autonomy state: per-colony decision + hive + task queue.
  const ai = world.ai;
  const tasks = world.tasks;
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, ai.priorityAt(cid));
    for (let nd = 0; nd < NEED_COUNT; nd++) h = fnvU32(h, ai.needAt(cid, nd as Need));
    const hives = ai.hiveCountAt(cid);
    h = fnvU32(h, hives);
    for (let k = 0; k < hives; k++) {
      h = fnvU32(h, ai.hiveXAt(cid, k));
      h = fnvU32(h, ai.hiveYAt(cid, k));
    }
    h = fnvU32(h, tasks.countOf(cid));
    for (let t = tasks.firstOf(cid); t !== NIL; t = tasks.nextOf(t)) {
      h = fnvU32(h, tasks.taskKindOf(t));
      h = fnvU32(h, tasks.taskTargetXOf(t));
      h = fnvU32(h, tasks.taskTargetYOf(t));
      h = fnvU32(h, tasks.taskAssignedToOf(t));
    }
  }
  h = fnvU32(h, ai.decisionsTotal);
  h = fnvU32(h, ai.hiveFoundedTotal);
  h = fnvU32(h, ai.splicesTotal);
  h = fnvU32(h, ai.scoutFallbacksTotal);
  h = fnvU32(h, ai.nurseEnqueuedTotal);
  h = fnvU32(h, ai.haulEnqueuedTotal);
  h = fnvU32(h, ai.repairEnqueuedTotal);
  h = fnvU32(h, tasks.enqueuedTotal);
  h = fnvU32(h, tasks.assignedTotal);
  h = fnvU32(h, tasks.completedTotal);
  h = fnvU32(h, tasks.droppedFullTotal);

  // Emergent-personality state (only when wired): per colony, the dominant trait,
  // the full trait score vector, the last recorded priority, and the kill counter
  // that feeds it — all integer + deterministic, so the personality loop is
  // load-bearing for the hash (a divergence in any colony's temperament shows up
  // here). Guarded so a sibling world without it stays byte-identical.
  const personality = world.personality;
  if (personality) {
    for (let cid = 0; cid < colonies.count; cid++) {
      h = fnvU32(h, personality.dominantOf(cid));
      for (let t = 0; t < TRAIT_COUNT; t++) h = fnvU32(h, personality.scoreOf(cid, t as Trait));
      h = fnvU32(h, personality.prevPriorityOf(cid));
      h = fnvU32(h, colonies.killsByAt(cid));
    }
    h = fnvU32(h, personality.recordsTotal);
    h = fnvU32(h, personality.emergencesTotal);
    h = fnvU32(h, personality.structuralTotal);
  }

  // Physical-hive state (only when a hive is wired): per colony, the chamber list
  // (type/x/y/level) and the territory tile count, then the global counters.
  const hive = world.hive;
  if (hive) {
    for (let cid = 0; cid < colonies.count; cid++) {
      const chambers = hive.chamberCountOf(cid);
      h = fnvU32(h, chambers);
      for (let k = 0; k < chambers; k++) {
        h = fnvU32(h, hive.chamberTypeAt(cid, k));
        h = fnvU32(h, hive.chamberXAt(cid, k));
        h = fnvU32(h, hive.chamberYAt(cid, k));
        h = fnvU32(h, hive.chamberLevelAt(cid, k));
        h = fnvU32(h, hive.chamberRepairAt(cid, k));
      }
      h = fnvU32(h, hive.territoryCountOf(cid));
    }
    h = fnvU32(h, hive.chambersBuiltTotal);
    h = fnvU32(h, hive.tilesClaimedTotal);
    h = fnvU32(h, hive.tunnelsCarvedTotal);
    h = fnvU32(h, hive.upgradesTotal);
    h = fnvU32(h, hive.mutationSplicesTotal);
    h = fnvU32(h, hive.storageYieldTotal);
    h = fnvU32(h, hive.militaryHitsTotal);
    h = fnvU32(h, hive.nurseBoostTotal);
    h = fnvU32(h, hive.haulMovedTotal);
    h = fnvU32(h, hive.repairedTotal);
  }

  // Live entities, in active-set order.
  const active = e.activeView();
  const n = e.aliveCount;
  h = fnvU32(h, n);
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    h = fnvU32(h, id);
    h = fnvU32(h, e.kind[id] as number);
    h = fnvU32(h, e.caste[id] as number);
    h = fnvU32(h, e.colony[id] as number);
    h = fnvU32(h, e.posX[id] as number);
    h = fnvU32(h, e.posY[id] as number);
    h = fnvU32(h, e.alive[id] as number);
    h = fnvU32(h, e.energy[id] as number);
    h = fnvU32(h, e.hp[id] as number);
    h = fnvU32(h, e.cargo[id] as number);
    h = fnvU32(h, e.state[id] as number);
    h = fnvU32(h, e.profileRef[id] as number);
    h = fnvU32(h, e.generation[id] as number);
    h = fnvU32(h, e.foodAmount[id] as number);
  }

  // Per-creature sub-tick movement accumulator — GUARDED on the opt-in feature, in the
  // SAME active-set order as the entity walk above. When the feature is OFF this block
  // is SKIPPED entirely, so folding nothing keeps the hash byte-identical to a pre-feature
  // world (even an all-zero moveAccum fold would be an extra fnvU32 per entity and shift
  // the hash). When ON it makes the carried sub-tile remainder load-bearing for the hash.
  // computeEcosystemStateHash calls this function, so it inherits the fold automatically —
  // it must NOT fold moveAccum again (no double-count).
  if (world.creatureSpeed) {
    for (let i = 0; i < n; i++) {
      h = fnvU32(h, e.moveAccum[active[i] as number] as number);
    }
  }

  // Gene state, in deterministic per-colony insertion order (list head→next).
  const genes = world.genes;
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, genes.countOf(cid));
    for (let gid = genes.firstOf(cid); gid !== NIL; gid = genes.nextOf(gid)) {
      h = fnvU32(h, genes.serialOf(gid));
      h = fnvU32(h, genes.typeOf(gid));
      h = fnvU32(h, genes.rarityOf(gid));
      h = fnvU32(h, genes.parentSerialOf(gid));
      h = fnvU32(h, genes.generationOf(gid));
      h = fnvU32(h, genes.originColonyOf(gid));
      h = fnvU32(h, genes.sourceCasteOf(gid));
      h = fnvU32(h, genes.originTickOf(gid));
      h = fnvU32(h, genes.traitTotalOf(gid));
      for (let t = 0; t < GENE_TRAIT_COUNT; t++) h = fnvU32(h, genes.traitOf(gid, t));
    }
  }
  h = fnvU32(h, genes.forgedTotal);
  h = fnvU32(h, genes.droppedFullTotal);

  // Leaderboard standings, in colony id order (the system writes only its own
  // store, so this fold can't change any other system's contribution above).
  const board = world.leaderboard;
  for (let cid = 0; cid < colonies.count; cid++) {
    h = fnvU32(h, board.totalAt(cid));
    h = fnvU32(h, board.scoreAt(cid));
    h = fnvU32(h, board.peakAt(cid));
    h = fnvU32(h, board.lifetimeAt(cid));
    h = fnvU32(h, board.rankAt(cid));
    for (let d = 0; d < SCORE_DIM_COUNT; d++) h = fnvU32(h, board.subscoreAt(cid, d as ScoreDim));
  }
  h = fnvU32(h, board.updates);

  // Dynamic-event state — GUARDED so an events-OFF world is byte-identical to a
  // pre-events hash (this whole block is skipped when no event system is wired).
  // SKIPPED for the ecosystem world (detected by its required `wildlife` field):
  // computeEcosystemStateHash folds the SAME event state itself, so folding here too
  // would double-count it and shift the ecosystem hash — this keeps the ecosystem
  // world's hash byte-identical. For the pure colony world this is the sole event fold.
  const events = world.events;
  if (events && !('wildlife' in world)) {
    h = fnvU32(h, events.activeEvent());
    h = fnvU32(h, events.remainingTicks());
    h = fnvU32(h, events.ticksToNextEvent());
    h = fnvU32(h, events.foodRegenPct());
    h = fnvU32(h, events.wildlifeCapacityPct());
    h = fnvU32(h, events.geneDropPct());
    h = fnvU32(h, events.organismDrain());
    h = fnvU32(h, events.eventsFiredTotal);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Debug rendering.
// ---------------------------------------------------------------------------

const CASTE_GLYPH: readonly string[] = ['w', 's', 'f', 'p']; // Worker/Soldier/Flyer/Specialist

/** Map glyph for each CHAMBER.* value: Nursery/Storage/Military/Mutation. */
const CHAMBER_GLYPH: readonly string[] = ['N', 'O', 'X', 'Y'];
/** Short label for each CHAMBER.* value (CLI census). */
const CHAMBER_NAME: readonly string[] = ['Nursery', 'Storage', 'Military', 'Mutation'];

/** Short label for each NEED.* value (CLI / census). */
const NEED_NAME: readonly string[] = ['Food', 'Defense', 'Expansion', 'Reproduction', 'Evolution'];

/**
 * ASCII map, layered bottom-up so the simulation reads at a glance:
 *   '#' wall · ':' claimed-but-empty hive territory · chamber letter
 *   (Nursery 'N', Storage 'O', Military 'X', Mutation 'Y') · '*' food ·
 *   caste letter for organisms (UPPER = colony 0).
 * The hive (territory + chambers) is drawn UNDER food/organisms so the physical
 * nest and its claimed ground are visible, but live units still show on top.
 */
export function renderColony(world: ColonyWorld): string {
  const { grid, entities } = world;
  const W = grid.width;
  const H = grid.height;
  const cells = new Array<string>(W * H);
  for (let i = 0; i < cells.length; i++) cells[i] = '.';

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!grid.isPassable(x, y)) cells[y * W + x] = '#';
    }
  }

  // Hive layers (only when a hive is wired): claimed-but-empty territory (a faint
  // glyph) lowest, then chamber centres — food/organisms overprint them.
  const hive = world.hive;
  if (hive) {
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (hive.territoryOwnerAt(x, y) !== NIL && grid.isPassable(x, y)) {
          cells[y * W + x] = ':';
        }
      }
    }
    for (let cid = 0; cid < world.colonies.count; cid++) {
      const chambers = hive.chamberCountOf(cid);
      for (let k = 0; k < chambers; k++) {
        const cx = hive.chamberXAt(cid, k);
        const cy = hive.chamberYAt(cid, k);
        if (grid.inBounds(cx, cy)) {
          cells[cy * W + cx] = CHAMBER_GLYPH[hive.chamberTypeAt(cid, k)] as string;
        }
      }
    }
  }

  const active = entities.activeView();
  const n = entities.aliveCount;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) === KIND.Food && (entities.foodAmount[id] as number) > 0) {
      cells[(entities.posY[id] as number) * W + (entities.posX[id] as number)] = '*';
    }
  }
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Organism) continue;
    let g = CASTE_GLYPH[entities.caste[id] as number] as string;
    if ((entities.colony[id] as number) === 0) g = g.toUpperCase();
    cells[(entities.posY[id] as number) * W + (entities.posX[id] as number)] = g;
  }

  let out = '';
  for (let y = 0; y < H; y++) out += cells.slice(y * W, y * W + W).join('') + '\n';
  return out;
}

/**
 * Per-caste evolved-genome FIELD GUIDE (CLI `--guide`): for each caste, the genes
 * filling its body-part slots (`BodyPart:GeneName`, fixed slot order), or
 * "(base — no genes)" when unevolved — the gene-driven biological identity that the
 * one-cell ASCII map glyph can't convey. The genome is per-caste (not per-organism), so
 * one row per caste describes every member of it. Pure read of `world.genomes.geneAt`;
 * debug/CLI only — no sim/tick/hash interaction.
 */
export function geneGuide(world: ColonyWorld): string {
  const g = world.genomes;
  let s = 'Field guide — evolved genome per caste:\n';
  for (let caste = 0; caste < CASTE_COUNT; caste++) {
    const parts: string[] = [];
    for (let slot = 0; slot < GENE_TYPE_COUNT; slot++) {
      const gene = g.geneAt(caste as Caste, slot as GeneType);
      if (gene) parts.push(`${GENE_TYPE_NAME[slot] as string}:${gene.name}`);
    }
    const body = parts.length > 0 ? parts.join(' ') : '(base — no genes)';
    s += `  ${(CASTE_NAME[caste] as string).padEnd(11)} ${body}\n`;
  }
  return s;
}

/**
 * Per-colony autonomy census (CLI `--ai`): the winning priority name, the full
 * need vector, hive-node count + coords, and the live task-queue depth. Walks in
 * deterministic colony order — debug/CLI only.
 */
export function aiCensus(world: ColonyWorld): string {
  const c = world.colonies;
  const ai = world.ai;
  const tasks = world.tasks;
  let s = '';
  for (let cid = 0; cid < c.count; cid++) {
    const prio = NEED_NAME[ai.priorityAt(cid)] as string;
    s += `  ai#${cid} prio=${prio.padEnd(12)} need[`;
    for (let nd = 0; nd < NEED_NAME.length; nd++) {
      s += `${NEED_NAME[nd] as string}=${ai.needAt(cid, nd as Need)}${nd < NEED_NAME.length - 1 ? ' ' : ''}`;
    }
    s += `] hives=${ai.hiveCountAt(cid)}`;
    const hives = ai.hiveCountAt(cid);
    if (hives > 0) {
      s += ' [';
      for (let k = 0; k < hives; k++) {
        s += `(${ai.hiveXAt(cid, k)},${ai.hiveYAt(cid, k)})${k < hives - 1 ? ' ' : ''}`;
      }
      s += ']';
    }
    s += ` tasks=${tasks.countOf(cid)}\n`;
  }
  return s;
}

/**
 * Per-colony personality census (CLI `--persona`): the crystallized dominant
 * trait NAME (or "forming"), plus the six raw trait scores. Walks in
 * deterministic colony order — debug/CLI only. This is the GOAL surface: two
 * colonies under different circumstances show as distinct emergent personalities.
 */
export function personaCensus(world: ColonyWorld): string {
  const c = world.colonies;
  const p = world.personality;
  if (!p) return '';
  let s = '';
  for (let cid = 0; cid < c.count; cid++) {
    const dom = p.dominantOf(cid);
    const name = dom === -1 ? 'forming' : (TRAIT_NAME[dom] as string);
    s += `  persona#${cid} ${name.padEnd(12)} [`;
    for (let t = 0; t < TRAIT_NAME.length; t++) {
      s += `${TRAIT_NAME[t] as string}=${p.scoreOf(cid, t as Trait)}${t < TRAIT_NAME.length - 1 ? ' ' : ''}`;
    }
    s += `] kills=${c.killsByAt(cid)}\n`;
  }
  return s;
}

/**
 * Per-colony physical-hive census (CLI `--hive`): each chamber's type-name +
 * level + centre, and the colony's claimed-tile count. Walks in deterministic
 * colony / chamber order — debug/CLI only.
 */
export function hiveCensus(world: ColonyWorld): string {
  const c = world.colonies;
  const hive = world.hive;
  if (!hive) return '';
  let s = '';
  for (let cid = 0; cid < c.count; cid++) {
    const chambers = hive.chamberCountOf(cid);
    s += `  hive#${cid} chambers=${chambers} territory=${hive.territoryCountOf(cid)}`;
    if (chambers > 0) {
      s += ' [';
      for (let k = 0; k < chambers; k++) {
        const name = CHAMBER_NAME[hive.chamberTypeAt(cid, k)] as string;
        s +=
          `${name}L${hive.chamberLevelAt(cid, k)}@(${hive.chamberXAt(cid, k)},` +
          `${hive.chamberYAt(cid, k)})${k < chambers - 1 ? ' ' : ''}`;
      }
      s += ']';
    }
    s += '\n';
  }
  return s;
}

/** One-line per-colony census for the verbose CLI / stats. */
export function colonyStats(world: ColonyWorld): string {
  const c = world.colonies;
  let s = '';
  for (let cid = 0; cid < c.count; cid++) {
    // Persistence tag: the protected player shows [queen] (only the queen left) or
    // [recovery] (clawing back) so the anti-extinction behaviour is legible.
    const tag = !c.isAlive(cid) ? ' [extinct]'
      : c.isProtected(cid) && c.populationAt(cid) === 0 ? ' [queen]'
      : c.isRecovering(cid) ? ' [recovery]'
      : '';
    s +=
      `  colony#${cid} pop=${String(c.populationAt(cid)).padStart(3)} ` +
      `food=${String(c.foodAt(cid)).padStart(4)} ` +
      `W=${c.casteCountAt(cid, CASTE.Worker)} S=${c.casteCountAt(cid, CASTE.Soldier)} ` +
      `F=${c.casteCountAt(cid, CASTE.Flyer)} P=${c.casteCountAt(cid, CASTE.Specialist)} ` +
      `gen=${c.generationAt(cid)}${tag}\n`;
  }
  return s;
}

/**
 * Per-colony gene census: total held, a count-by-rarity breakdown, and the
 * describeGene line(s) for the 1–2 highest-rarity genes the colony holds.
 * Walks the colony's gene list (deterministic order) — debug/CLI only.
 */
export function geneCensus(world: ColonyWorld): string {
  const g = world.genes;
  const c = world.colonies;
  let s = '';
  const byRarity = new Int32Array(RARITY_COUNT);
  for (let cid = 0; cid < c.count; cid++) {
    byRarity.fill(0);
    // Track the two highest-rarity genes (ties → first seen in walk order).
    let best: GeneId = NIL;
    let second: GeneId = NIL;
    for (let id = g.firstOf(cid); id !== NIL; id = g.nextOf(id)) {
      byRarity[g.rarityOf(id)] = (byRarity[g.rarityOf(id)] as number) + 1;
      if (best === NIL || g.rarityOf(id) > g.rarityOf(best)) {
        second = best;
        best = id;
      } else if (second === NIL || g.rarityOf(id) > g.rarityOf(second)) {
        second = id;
      }
    }
    s += `  genes#${cid} total=${String(g.countOf(cid)).padStart(3)} [`;
    for (let r = 0; r < RARITY_COUNT; r++) {
      s += `${RARITY_NAME[r] as string}=${byRarity[r] as number}${r < RARITY_COUNT - 1 ? ' ' : ''}`;
    }
    s += ']\n';
    if (best !== NIL) s += `      top: ${g.describeGene(best)}\n`;
    if (second !== NIL) s += `      2nd: ${g.describeGene(second)}\n`;
  }
  return s;
}

/**
 * Leaderboard census (CLI `--board`): one line per colony in RANK order (leader
 * first), showing the ranked total, the live instant + peak, and the six raw
 * subscores. Deterministic (walks ranks 0..count-1) — debug/CLI only.
 */
export function leaderboardCensus(world: ColonyWorld): string {
  const c = world.colonies;
  const b = world.leaderboard;
  let s = '';
  for (let r = 0; r < c.count; r++) {
    // Find the colony holding rank r (colonies are few; linear scan is fine).
    let cid = -1;
    for (let i = 0; i < c.count; i++) {
      if (b.rankAt(i) === r) {
        cid = i;
        break;
      }
    }
    if (cid < 0) continue;
    s +=
      `  #${r} colony#${cid} total=${b.totalAt(cid)} ` +
      `(now=${b.scoreAt(cid)} peak=${b.peakAt(cid)}) ` +
      `[GC=${b.subscoreAt(cid, SCORE_DIM.GenomeComplexity)} ` +
      `GR=${b.subscoreAt(cid, SCORE_DIM.GeneRarity)} ` +
      `Terr=${b.subscoreAt(cid, SCORE_DIM.TerritoryControl)} ` +
      `Size=${b.subscoreAt(cid, SCORE_DIM.ColonySize)} ` +
      `Eco=${b.subscoreAt(cid, SCORE_DIM.EcosystemInfluence)} ` +
      `Reg=${b.subscoreAt(cid, SCORE_DIM.RegionDominance)} ` +
      `Disc=${b.subscoreAt(cid, SCORE_DIM.Discovery)}]\n`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// CLI wrapper.
// ---------------------------------------------------------------------------

interface CliFlags {
  seed: number; width: number; height: number; colonies: number; members: number;
  food: number; ticks: number; every: number; quiet: boolean; map: boolean;
  genes: boolean; ai: boolean; legs: boolean; board: boolean; hive: boolean;
  persona: boolean; guide: boolean;
  // Queen-persistence tuning (default = DEFAULT_QUEEN_CONFIG ⇒ no-flag runs byte-identical).
  queenCritical: number; queenRecoveryCost: number; queenRecoveryCd: number; queenEmergencyCd: number;
}

function parseFlags(argv: readonly string[]): CliFlags {
  const f: CliFlags = {
    seed: 1, width: 32, height: 18, colonies: 2, members: 8,
    food: 14, ticks: 200, every: 25, quiet: false, map: false,
    genes: false, ai: false, legs: false, board: false, hive: false,
    persona: false, guide: false,
    queenCritical: DEFAULT_QUEEN_CONFIG.criticalPopulation,
    queenRecoveryCost: DEFAULT_QUEEN_CONFIG.recoverySpawnCost,
    queenRecoveryCd: DEFAULT_QUEEN_CONFIG.recoveryCooldown,
    queenEmergencyCd: DEFAULT_QUEEN_CONFIG.emergencyCooldown,
  };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i] as string;
    switch (a) {
      case '--seed': f.seed = intArg(argv, ++i, f.seed); break;
      case '--width': f.width = intArg(argv, ++i, f.width); break;
      case '--height': f.height = intArg(argv, ++i, f.height); break;
      case '--colonies': f.colonies = intArg(argv, ++i, f.colonies); break;
      case '--members': f.members = intArg(argv, ++i, f.members); break;
      case '--food': f.food = intArg(argv, ++i, f.food); break;
      case '--ticks': f.ticks = intArg(argv, ++i, f.ticks); break;
      case '--every': f.every = intArg(argv, ++i, f.every); break;
      case '--quiet': f.quiet = true; break;
      case '--map': f.map = true; break;
      case '--genes': f.genes = true; break;
      case '--ai': f.ai = true; break;
      case '--legs': f.legs = true; break;
      case '--board': f.board = true; break;
      case '--hive': f.hive = true; break;
      case '--persona': f.persona = true; break;
      case '--guide': f.guide = true; break;
      case '--queen-critical': f.queenCritical = intArg(argv, ++i, f.queenCritical); break;
      case '--queen-recovery-cost': f.queenRecoveryCost = intArg(argv, ++i, f.queenRecoveryCost); break;
      case '--queen-recovery-cd': f.queenRecoveryCd = intArg(argv, ++i, f.queenRecoveryCd); break;
      case '--queen-emergency-cd': f.queenEmergencyCd = intArg(argv, ++i, f.queenEmergencyCd); break;
      default: break;
    }
  }
  return f;
}

function intArg(argv: readonly string[], i: number, fallback: number): number {
  const raw = argv[i];
  if (raw === undefined) return fallback;
  const v = Number.parseInt(raw, 10);
  return Number.isFinite(v) ? v : fallback;
}

function configFromFlags(f: CliFlags): ColonyWorldConfig {
  return {
    seed: f.seed, width: f.width, height: f.height,
    colonies: f.colonies, startMembers: f.members,
    // foodAmount 12 → 18: with slower regen (defaultResourceConfig) a richer
    // initial cache gives early colonies something to live on while supply chains
    // form, so the slowdown reads as scarcity-with-depth, not instant famine.
    food: f.food, foodAmount: 18, maxPerTile: 4,
    castes: defaultCasteConfigs(), colony: defaultColonyConfig(),
    resource: defaultResourceConfig(),
    // Queen-persistence (defaults to DEFAULT_QUEEN_CONFIG values ⇒ a no-flag run is
    // byte-identical; --queen-* flags let balance work sweep criticalPopulation/costs).
    queen: {
      criticalPopulation: f.queenCritical, recoverySpawnCost: f.queenRecoveryCost,
      recoveryCooldown: f.queenRecoveryCd, emergencyCooldown: f.queenEmergencyCd,
    },
  };
}

function runCli(argv: readonly string[]): void {
  const flags = parseFlags(argv);
  const config = configFromFlags(flags);
  const world = buildColonyWorld(config);

  // Headline gene demo: slot a leg gene into the Worker genome. Workers spawned
  // BEFORE this keep their speed; workers spawned AFTER inherit the boost.
  if (flags.legs) {
    const before = world.genomes.currentProfile(CASTE.Worker).stepsPerTick;
    const gene = legGene();
    world.genomes.addGene(CASTE.Worker, gene);
    const after = world.genomes.currentProfile(CASTE.Worker).stepsPerTick;
    console.log(
      `gene: slotted "${gene.name}" into Worker genome — ` +
        `stepsPerTick ${before} → ${after} (future workers are faster)\n`,
    );
  }

  if (flags.quiet) {
    const start = process.hrtime.bigint();
    runHeadless(world, flags.ticks);
    const elapsedMs = Number(process.hrtime.bigint() - start) / 1e6;
    console.log(
      `quiet: colonies=${flags.colonies} ticks=${flags.ticks} grid=${flags.width}x${flags.height} | ` +
        `elapsed=${elapsedMs.toFixed(2)}ms ticks/sec=${(flags.ticks / (elapsedMs / 1000)).toFixed(0)} ` +
        `organisms=${world.colonies.totalPopulation()} food=${world.colonies.totalFood()} ` +
        `${geneTotals(world)} ${aiTotals(world)} ${personaTotals(world)} ${boardTotals(world)} ${hiveTotals(world)} ` +
        `hash=${hashHex(computeColonyStateHash(world))}`,
    );
    return;
  }

  const small = world.grid.width <= 64 && world.grid.height <= 40;
  printFrame(world, flags, small);
  let done = 0;
  while (done < flags.ticks) {
    const chunk = Math.min(flags.every, flags.ticks - done);
    runHeadless(world, chunk);
    done += chunk;
    printFrame(world, flags, small);
  }
  console.log(
    `\nsummary: seed=${flags.seed} ticks=${world.loop.tick} ` +
      `spawned=${world.colonies.spawnedTotal} deaths=${world.colonies.deathsTotal} ` +
      `extinct=${world.colonies.extinctTotal} queenRescues=${world.colonies.queenRescuesTotal} ` +
      `deposited=${world.colonies.depositedTotal} ` +
      `${geneTotals(world)} ${aiTotals(world)} ${boardTotals(world)} ${hiveTotals(world)} ` +
      `finalHash=${hashHex(computeColonyStateHash(world))}`,
  );
}

/** Compact gene-totals fragment for summary lines. */
function geneTotals(world: ColonyWorld): string {
  const g = world.genes;
  return (
    `genes=${g.forgedTotal} combat=${g.combatDropsTotal} ` +
    `death=${g.deathDropsTotal} full=${g.droppedFullTotal}`
  );
}

/** Compact autonomy-totals fragment for summary lines. */
function aiTotals(world: ColonyWorld): string {
  const ai = world.ai;
  const t = world.tasks;
  return (
    `decisions=${ai.decisionsTotal} hives=${ai.hiveFoundedTotal}(fb=${ai.scoutFallbacksTotal}) ` +
    `splices=${ai.splicesTotal} ` +
    `tasks(enq=${t.enqueuedTotal} asn=${t.assignedTotal} done=${t.completedTotal} ` +
    `nurse=${ai.nurseEnqueuedTotal} haul=${ai.haulEnqueuedTotal} repair=${ai.repairEnqueuedTotal})`
  );
}

/**
 * Compact personality fragment for summary lines: the dominant-trait first
 * letter per colony (in colony order; '-' while forming), plus an emerged count.
 * Shows at a glance that distinct personalities have crystallized.
 */
function personaTotals(world: ColonyWorld): string {
  const c = world.colonies;
  const p = world.personality;
  if (!p) return '';
  let letters = '';
  for (let cid = 0; cid < c.count; cid++) {
    const dom = p.dominantOf(cid);
    letters += dom === -1 ? '-' : (TRAIT_NAME[dom] as string).charAt(0);
  }
  return `personas=${letters} emerged=${p.emergencesTotal}`;
}

/** Compact leaderboard fragment for summary lines (current leader + its total). */
function boardTotals(world: ColonyWorld): string {
  const b = world.leaderboard;
  const leader = b.leaderId();
  const score = leader === NIL ? 0 : b.totalAt(leader);
  return `boardLeader=c${leader} score=${score}`;
}

/** Compact physical-hive-totals fragment for summary lines. */
function hiveTotals(world: ColonyWorld): string {
  const h = world.hive;
  if (!h) return '';
  return (
    `chambers=${h.chambersBuiltTotal} tiles=${h.tilesClaimedTotal} ` +
    `tunnels=${h.tunnelsCarvedTotal} upgrades=${h.upgradesTotal} musplice=${h.mutationSplicesTotal} ` +
    `yield=${h.storageYieldTotal} mil=${h.militaryHitsTotal}`
  );
}

function printFrame(world: ColonyWorld, flags: CliFlags, small: boolean): void {
  console.log(
    `tick=${String(world.loop.tick).padStart(4)} ` +
      `organisms=${String(world.colonies.totalPopulation()).padStart(4)} ` +
      `foodStored=${String(world.colonies.totalFood()).padStart(5)} ` +
      `deaths=${String(world.colonies.deathsTotal).padStart(4)}`,
  );
  process.stdout.write(colonyStats(world));
  if (flags.genes) process.stdout.write(geneCensus(world));
  if (flags.ai) process.stdout.write(aiCensus(world));
  if (flags.persona) process.stdout.write(personaCensus(world));
  if (flags.board) process.stdout.write(leaderboardCensus(world));
  if (flags.hive) process.stdout.write(hiveCensus(world));
  if (flags.guide) process.stdout.write(geneGuide(world));
  if (flags.map && small) process.stdout.write(renderColony(world));
}

const isMain =
  typeof process !== 'undefined' &&
  Array.isArray(process.argv) &&
  process.argv[1] !== undefined &&
  import.meta.url === `file://${process.argv[1]}`;

if (isMain) runCli(process.argv.slice(2));
